import { Copy, Share2, Download, Printer, FileText, RotateCcw, Bookmark, BookmarkCheck } from "lucide-react";
import { toast } from "sonner";
import { copyResults, shareResults, exportCSV, printPDF } from "@/lib/export";
import { toggleFavorite, isFavorite } from "@/lib/storage";
import { useState, useEffect } from "react";

/**
 * Reusable results panel with action bar (copy, share, csv, pdf, reset, fav).
 * @param {Array<[string,string]>} rows – label/value pairs to display and export
 */
export default function ResultsPanel({ title, rows, highlight, onReset, note, slug }) {
  const [fav, setFav] = useState(false);
  useEffect(() => { if (slug) setFav(isFavorite(slug)); }, [slug]);

  const handleCopy = async () => {
    const ok = await copyResults(rows);
    if (ok) toast.success("Results copied to clipboard");
    else toast.error("Copy failed");
  };
  const handleShare = async () => {
    const ok = await shareResults(title, rows);
    if (ok) toast.success("Shared / copied");
  };
  const handleCSV = () => { exportCSV(title, rows); toast.success("CSV downloaded"); };
  const handlePDF = () => { printPDF(title, rows, note); toast.success("Opening PDF preview"); };
  const handleFav = () => {
    if (!slug) return;
    toggleFavorite(slug);
    const nowFav = isFavorite(slug);
    setFav(nowFav);
    toast.success(nowFav ? "Added to favorites" : "Removed from favorites");
  };

  return (
    <div className="rounded-lg border border-primary/25 bg-primary/[0.03] p-5 sm:p-6" data-testid="results-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="overline text-primary/80">Results</div>
        {slug && (
          <button
            onClick={handleFav}
            aria-label="Toggle favorite"
            data-testid="fav-toggle-btn"
            className="h-8 w-8 rounded-md hover:bg-primary/10 flex items-center justify-center transition text-primary"
          >
            {fav ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" strokeWidth={1.75} />}
          </button>
        )}
      </div>

      {highlight && (
        <div className="mb-5 pb-5 border-b border-primary/15" data-testid="results-highlight">
          <div className="overline mb-1">{highlight.label}</div>
          <div className="text-3xl sm:text-4xl font-black tracking-tighter text-primary stat-num">
            {highlight.value}
          </div>
          {highlight.sub && <div className="text-xs text-muted-foreground mt-1">{highlight.sub}</div>}
        </div>
      )}

      <div className="space-y-2.5" data-testid="results-list">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-muted-foreground">{k}</span>
            <span className="stat-num text-right font-medium">{v}</span>
          </div>
        ))}
      </div>

      {note && (
        <div className="mt-5 pt-4 border-t border-primary/15 text-[11px] text-muted-foreground leading-relaxed" data-testid="results-note">
          {note}
        </div>
      )}

      <div className="mt-5 pt-4 border-t border-primary/15 flex flex-wrap gap-2 no-print">
        <button data-testid="copy-btn" onClick={handleCopy} className="h-9 px-3 rounded-md border border-border bg-card hover:bg-accent text-xs font-medium flex items-center gap-1.5 transition">
          <Copy className="h-3.5 w-3.5" strokeWidth={1.75} /> Copy
        </button>
        <button data-testid="share-btn" onClick={handleShare} className="h-9 px-3 rounded-md border border-border bg-card hover:bg-accent text-xs font-medium flex items-center gap-1.5 transition">
          <Share2 className="h-3.5 w-3.5" strokeWidth={1.75} /> Share
        </button>
        <button data-testid="csv-btn" onClick={handleCSV} className="h-9 px-3 rounded-md border border-border bg-card hover:bg-accent text-xs font-medium flex items-center gap-1.5 transition">
          <Download className="h-3.5 w-3.5" strokeWidth={1.75} /> CSV
        </button>
        <button data-testid="pdf-btn" onClick={handlePDF} className="h-9 px-3 rounded-md border border-border bg-card hover:bg-accent text-xs font-medium flex items-center gap-1.5 transition">
          <FileText className="h-3.5 w-3.5" strokeWidth={1.75} /> PDF
        </button>
        <button data-testid="print-btn" onClick={() => window.print()} className="h-9 px-3 rounded-md border border-border bg-card hover:bg-accent text-xs font-medium flex items-center gap-1.5 transition">
          <Printer className="h-3.5 w-3.5" strokeWidth={1.75} /> Print
        </button>
        {onReset && (
          <button data-testid="reset-btn" onClick={onReset} className="ml-auto h-9 px-3 rounded-md text-xs font-medium flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition">
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} /> Reset
          </button>
        )}
      </div>
    </div>
  );
}

export function CalcPageHeader({ title, description, slug }) {
  return (
    <div className="mb-6 md:mb-8" data-testid="calc-page-header">
      <div className="overline mb-2">Calculator</div>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">{title}</h1>
      {description && <p className="text-muted-foreground mt-2 max-w-2xl text-sm sm:text-base">{description}</p>}
    </div>
  );
}

export function Field({ label, hint, children, testId }) {
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <label className="overline">{label}</label>
      {children}
      {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}
